import type { SlotName, SlotQuestion } from "./types";

/** How many choices a follow-up offers. */
const CHOICE_COUNT = 6;

/** Lowercase words without punctuation or a leading "the", for comparing names. */
export const normal = (text: string) =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^the /, "");

/** The items with that name: an exact match, otherwise every name starting with it or holding all its words. */
export function findByName<T>(items: T[], wanted: string, nameOf: (item: T) => string): T[] {
  const target = normal(wanted);
  const exact = items.filter((item) => normal(nameOf(item)) === target);
  if (exact.length > 0) return exact;
  const words = target.split(" ");
  return items.filter((item) => {
    const name = normal(nameOf(item));
    const nameWords = name.split(" ");
    return name.startsWith(target) || words.every((w) => nameWords.includes(w));
  });
}

/** The items whose whole name appears in the text; the longest names win ("Grade 2B" over "Grade 2"). */
export function findNamedIn<T>(items: T[], text: string, nameOf: (item: T) => string): T[] {
  const padded = ` ${normal(text)} `;
  const named = items.filter((item) => {
    const name = normal(nameOf(item));
    return name && padded.includes(` ${name} `);
  });
  const longest = Math.max(0, ...named.map((item) => normal(nameOf(item)).length));
  return named.filter((item) => normal(nameOf(item)).length === longest);
}

/** Words said around a name that are never part of it: "my grade 4 class", "the Jumpstart parents". */
const AROUND_A_NAME = new Set([
  "my", "our", "the", "a", "class", "classes", "parents", "families", "learners", "kids", "children", "group",
  "circle", "all", "of", "in", "lot", "bunch",
]);
const NUMBER_WORDS: Record<string, string> = {
  one: "1", two: "2", three: "3", four: "4", five: "5", six: "6", seven: "7", eight: "8", nine: "9", ten: "10",
  eleven: "11", twelve: "12",
};
/** "the whole school", "all parents", "everybody": the Circle everyone is in, when there is one. */
const MEANS_EVERYONE = /^(?:(?:the\s+)?whole\s+school|(?:all\s+(?:the\s+)?)?(?:parents|families)|all|every(?:one|body)|all\s+of\s+(?:them|us))$/;

const looseWords = (text: string) =>
  normal(text)
    .split(" ")
    .map((w) => (w === "gr" || w === "grd" || w === "gr." ? "grade" : NUMBER_WORDS[w] ?? w))
    .map((w) => w.replace(/^gr(?:ade)?(\d+)$/, "grade $1"))
    .join(" ")
    .split(" ")
    .filter((w) => w && !AROUND_A_NAME.has(w));

/**
 * A second, looser try once the name said isn't found as typed: everyday words dropped and "gr 4" or
 * "grade four" read as "grade 4". Every word left must be in the name, so "grade 4" finds
 * "TEST Grade 4 2027" but never "Grade 5".
 */
export function namedLoosely<T>(items: T[], wanted: string, nameOf: (item: T) => string): T[] {
  if (MEANS_EVERYONE.test(normal(wanted))) return items.filter((item) => /^every(?:one|body)$/.test(normal(nameOf(item))));
  const words = looseWords(wanted);
  if (words.length === 0) return [];
  return items.filter((item) => {
    const nameWords = looseWords(nameOf(item));
    return words.every((w) => nameWords.includes(w));
  });
}

export const askFor = (
  slot: SlotName,
  reason: SlotQuestion["reason"],
  text: string,
  choices: string[]
): SlotQuestion => ({ kind: "ask", slot, reason, text, choices: choices.slice(0, CHOICE_COUNT) });
